/**
 * JuridicoPage — hub de Impugnação e Defesa (F7).
 *
 * Réplica dos juridico_impugnacao.html / juridico_defesa.html num só lugar:
 * as duas peças têm o mesmo ciclo (gerar → acompanhar → ler → exportar) e
 * diferem só na origem — a impugnação nasce de uma auditoria concluída; a
 * defesa, do edital + a impugnação recebida.
 */

import {
  faBalanceScale,
  faCheckCircle,
  faExclamationTriangle,
  faEye,
  faFileUpload,
  faGavel,
  faScaleBalanced,
  faShieldAlt,
  faSpinner,
  faTrashAlt,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  apontamentosDaAuditoria,
  auditoriasDisponiveis,
  excluirPeca,
  gerarDefesa,
  gerarImpugnacao,
  listarPecas,
  type TipoPeca,
} from '../../api/juridico'
import { useToast } from '../../components/Toast'

const CONFIGURACAO: Record<TipoPeca, {
  titulo: string
  subtitulo: string
  cabecalho: string
  botao: string
}> = {
  impugnacao: {
    titulo: 'Impugnação ao Edital',
    subtitulo:
      'A peça nasce dos apontamentos de uma auditoria concluída (Art. 164 da Lei 14.133/2021).',
    cabecalho: 'from-blue-600 to-indigo-700',
    botao: 'from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800',
  },
  defesa: {
    titulo: 'Defesa contra Impugnação',
    subtitulo:
      'A peça responde a uma impugnação recebida, a partir do edital atacado.',
    cabecalho: 'from-emerald-600 to-teal-700',
    botao: 'from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800',
  },
}

function ListaPecas({ tipo }: { tipo: TipoPeca }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const { data, isLoading } = useQuery({
    queryKey: ['juridico-pecas', tipo],
    queryFn: () => listarPecas(tipo),
  })

  const excluir = useMutation({
    mutationFn: (id: number) => excluirPeca(tipo, id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['juridico-pecas', tipo] })
      notificar('success', 'Documento excluído.')
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="text-sm text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando…
      </p>
    )
  }
  const pecas = data?.pecas ?? []
  if (pecas.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600">
        Nenhum documento gerado ainda.
      </p>
    )
  }

  return (
    <ul className="space-y-3">
      {pecas.map((peca) => {
        const pronta = peca.status === 'GERADA'
        const falhou = peca.status === 'ERRO' || !!peca.erro
        return (
          <li
            key={peca.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-dark-card"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                {peca.titulo}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {peca.criado_em} ·{' '}
                <span
                  className={
                    falhou
                      ? 'text-red-600 dark:text-red-400'
                      : pronta
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-amber-600 dark:text-amber-400'
                  }
                >
                  {falhou ? 'Erro na geração' : pronta ? 'Pronta' : 'Em geração'}
                </span>
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              {pronta ? (
                <Link
                  to={`/juridico/${tipo}/${peca.id}`}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
                >
                  <FontAwesomeIcon icon={faEye} />
                  Ver
                </Link>
              ) : (
                <Link
                  to={`/tasks/${tipo}/${peca.id}`}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300"
                >
                  <FontAwesomeIcon icon={faSpinner} spin={!falhou} />
                  Acompanhar
                </Link>
              )}
              <button
                type="button"
                onClick={() => excluir.mutate(peca.id)}
                aria-label={`Excluir ${peca.titulo}`}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:border-slate-600 dark:hover:bg-red-900/20"
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function FormularioImpugnacao() {
  const navegar = useNavigate()
  const { notificar } = useToast()
  const [auditId, setAuditId] = useState<number | null>(null)
  const [campos, setCampos] = useState<Record<string, string>>({})

  const { data: auditorias } = useQuery({
    queryKey: ['juridico-auditorias'],
    queryFn: auditoriasDisponiveis,
  })
  const { data: apontamentos } = useQuery({
    queryKey: ['juridico-apontamentos', auditId],
    queryFn: () => apontamentosDaAuditoria(auditId!),
    enabled: auditId !== null,
    retry: false,
  })

  const gerar = useMutation({
    mutationFn: () =>
      gerarImpugnacao({
        audit_id: auditId!,
        empresa_nome: campos.empresa_nome ?? '',
        empresa_cnpj: campos.empresa_cnpj ?? '',
        empresa_endereco: campos.empresa_endereco ?? '',
        representante_nome: campos.representante_nome ?? '',
        representante_cpf: campos.representante_cpf ?? '',
        orgao_nome: campos.orgao_nome ?? '',
        numero_processo: campos.numero_processo ?? '',
        numero_certame: campos.numero_certame ?? '',
        localidade: campos.localidade ?? '',
        descricao_objeto: campos.descricao_objeto ?? '',
        valor_estimado: campos.valor_estimado ?? '',
        data_publicacao: campos.data_publicacao ?? '',
        data_sessao: campos.data_sessao ?? '',
        data_prazo_impugnacao: campos.data_prazo_impugnacao ?? '',
      }),
    onSuccess: (r) => navegar(`/tasks/impugnacao/${r.id}`),
    onError: (e: Error) => notificar('error', e.message),
  })

  const entrada =
    'w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'
  const definir = (campo: string, valor: string) =>
    setCampos((atuais) => ({ ...atuais, [campo]: valor }))

  const CAMPOS: [string, string, boolean][] = [
    ['empresa_nome', 'Razão social da impugnante', true],
    ['empresa_cnpj', 'CNPJ', false],
    ['empresa_endereco', 'Endereço', false],
    ['representante_nome', 'Representante legal', false],
    ['representante_cpf', 'CPF do representante', false],
    ['orgao_nome', 'Órgão licitante', true],
    ['numero_processo', 'Número do processo', false],
    ['numero_certame', 'Número do certame', false],
    ['localidade', 'Localidade', false],
    ['valor_estimado', 'Valor estimado', false],
    ['data_publicacao', 'Data de publicação', false],
    ['data_sessao', 'Data da sessão', false],
    ['data_prazo_impugnacao', 'Prazo para impugnar', false],
  ]

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        if (!auditId) {
          notificar('error', 'Escolha a auditoria que embasa a impugnação.')
          return
        }
        gerar.mutate()
      }}
    >
      <div>
        <label
          htmlFor="audit_id"
          className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          Auditoria de origem <span className="text-red-500">*</span>
        </label>
        <select
          id="audit_id"
          name="audit_id"
          value={auditId ?? ''}
          onChange={(e) => setAuditId(e.target.value ? Number(e.target.value) : null)}
          className={entrada}
        >
          <option value="">-- Selecione uma auditoria concluída --</option>
          {(auditorias?.auditorias ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              #{a.id} — {a.arquivo} ({a.criado_em})
            </option>
          ))}
        </select>
        {(auditorias?.auditorias ?? []).length === 0 && (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Nenhuma auditoria concluída. A impugnação parte dos apontamentos de
            uma auditoria.
          </p>
        )}
      </div>

      {apontamentos && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <p className="mb-2 text-sm font-semibold text-blue-800 dark:text-blue-300">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
            {apontamentos.apontamentos.length} apontamento(s) serão usados como
            argumentos
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto text-sm text-blue-700 dark:text-blue-400">
            {apontamentos.apontamentos.slice(0, 8).map((a, i) => (
              <li key={i}>• {a.titulo}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {CAMPOS.map(([campo, rotulo, obrigatorio]) => (
          <div key={campo}>
            <label
              htmlFor={campo}
              className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {rotulo}
              {obrigatorio && <span className="ml-1 text-red-500">*</span>}
            </label>
            <input
              id={campo}
              name={campo}
              type="text"
              required={obrigatorio}
              value={campos[campo] ?? ''}
              onChange={(e) => definir(campo, e.target.value)}
              className={entrada}
            />
          </div>
        ))}
        <div className="md:col-span-2">
          <label
            htmlFor="descricao_objeto"
            className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            Objeto da licitação
          </label>
          <textarea
            id="descricao_objeto"
            name="descricao_objeto"
            rows={3}
            value={campos.descricao_objeto ?? ''}
            onChange={(e) => definir('descricao_objeto', e.target.value)}
            className={entrada}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={gerar.isPending}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-6 py-3 font-semibold text-white shadow-lg transition-all disabled:opacity-60 ${CONFIGURACAO.impugnacao.botao}`}
      >
        <FontAwesomeIcon
          icon={gerar.isPending ? faSpinner : faWandMagicSparkles}
          spin={gerar.isPending}
        />
        Gerar Impugnação
      </button>
    </form>
  )
}

function FormularioDefesa() {
  const navegar = useNavigate()
  const { notificar } = useToast()
  const [edital, setEdital] = useState<File | null>(null)
  const [impugnacao, setImpugnacao] = useState<File | null>(null)

  const gerar = useMutation({
    mutationFn: () => gerarDefesa(edital!, impugnacao!),
    onSuccess: (r) => navegar(`/tasks/defesa/${r.id}`),
    onError: (e: Error) => notificar('error', e.message),
  })

  const arquivo =
    'block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-emerald-700 hover:file:bg-emerald-100 dark:text-slate-300'

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault()
        if (!edital || !impugnacao) {
          notificar('error', 'Envie o edital e a impugnação recebida.')
          return
        }
        gerar.mutate()
      }}
    >
      <div>
        <label
          htmlFor="edital"
          className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          Edital atacado <span className="text-red-500">*</span>
        </label>
        <input
          id="edital"
          name="edital"
          type="file"
          accept=".pdf,.docx,.doc,.txt"
          onChange={(e) => setEdital(e.target.files?.[0] ?? null)}
          className={arquivo}
        />
      </div>
      <div>
        <label
          htmlFor="impugnacao"
          className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          Impugnação recebida <span className="text-red-500">*</span>
        </label>
        <input
          id="impugnacao"
          name="impugnacao"
          type="file"
          accept=".pdf,.docx,.doc,.txt"
          onChange={(e) => setImpugnacao(e.target.files?.[0] ?? null)}
          className={arquivo}
        />
      </div>
      <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-dark-bg/40 dark:text-slate-400">
        <FontAwesomeIcon icon={faFileUpload} className="mr-2" />
        A leitura dos dois arquivos acontece no servidor antes de a geração
        começar — PDFs digitalizados podem levar mais tempo.
      </p>
      <button
        type="submit"
        disabled={gerar.isPending}
        className={`inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-6 py-3 font-semibold text-white shadow-lg transition-all disabled:opacity-60 ${CONFIGURACAO.defesa.botao}`}
      >
        <FontAwesomeIcon
          icon={gerar.isPending ? faSpinner : faWandMagicSparkles}
          spin={gerar.isPending}
        />
        {gerar.isPending ? 'Lendo os arquivos…' : 'Gerar Defesa'}
      </button>
    </form>
  )
}

export function JuridicoPage() {
  const { tipo } = useParams()
  const atual: TipoPeca = tipo === 'defesa' ? 'defesa' : 'impugnacao'
  const config = CONFIGURACAO[atual]

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <div className={`bg-gradient-to-r p-6 text-white ${config.cabecalho}`}>
          <h1 className="flex items-center text-2xl font-bold">
            <FontAwesomeIcon
              icon={atual === 'impugnacao' ? faGavel : faShieldAlt}
              className="mr-3"
            />
            {config.titulo}
          </h1>
          <p className="mt-1 text-white/80">{config.subtitulo}</p>
        </div>
        <nav className="flex border-b border-slate-200 dark:border-slate-700">
          {(['impugnacao', 'defesa'] as TipoPeca[]).map((t) => (
            <Link
              key={t}
              to={`/juridico/${t}`}
              className={`flex items-center gap-2 border-b-2 px-6 py-3 text-sm font-medium transition-colors ${
                atual === t
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400'
              }`}
            >
              <FontAwesomeIcon icon={t === 'impugnacao' ? faGavel : faShieldAlt} />
              {t === 'impugnacao' ? 'Impugnação' : 'Defesa'}
            </Link>
          ))}
        </nav>
      </header>

      <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faScaleBalanced} className="mr-2 text-indigo-600" />
          Nova peça
        </h2>
        {atual === 'impugnacao' ? <FormularioImpugnacao /> : <FormularioDefesa />}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faBalanceScale} className="mr-2 text-indigo-600" />
          Documentos gerados
        </h2>
        <ListaPecas tipo={atual} />
      </section>
    </div>
  )
}
